import { appAuthAtom, BaseResource, LanguageType } from '@eih-netbroker/common';
import { useCallback, useContext, useMemo } from 'react';
import { AccountContext } from '../../context/AccountContext';
import { AxiosInstanceContext } from '../../context/AxiosInstanceContext';
import { Config } from '../config/config';
import { store } from '../state/store';
import { uuid } from '../utils/uuidUtils';

type ConstructorType<T extends BaseResource> = new (...args: any[]) => T;

export type ResourceHeaders = {
  readonly id: string;
  readonly correlid: string;
  readonly source: string;
  readonly srcModule: string;
  readonly acceptLanguage: string;
  readonly channel: string;
  readonly transactionid: string;
};

export type ResourceHeadersOfMultiAccountRequest = Omit<ResourceHeadersWithToken, 'accountId'> & {
  readonly accountId: string[];
};

export type ResourceHeadersWithToken = ResourceHeaders & {
  readonly userid: string;
  readonly accountId: string;
};

const classes: { [key: string]: BaseResource } = {};

export const useResource = <T extends BaseResource>(resourceClass: ConstructorType<T>): T => {
  const axiosInstanceContext = useContext(AxiosInstanceContext);

  if (!classes[resourceClass.name]) {
    const axiosInstance = axiosInstanceContext?.getAxiosInstance();
    classes[resourceClass.name] = new resourceClass(axiosInstance, Config.API.usUrl.baseUrl, Config.API);
  }

  return useMemo(() => classes[resourceClass.name] as T, [resourceClass.name]);
};

export const useResourceHeaders = () => {
  const accountContext = useContext(AccountContext);

  const accountNumber = useMemo(() => {
    return accountContext?.selectedAccountOrClient?.account?.accountNumber ?? '';
  }, [accountContext?.selectedAccountOrClient]);

  const accountNumberForMultiAccount = useMemo(() => {
    if (accountContext?.selectedAccountOrClient?.account?.accountNumber) {
      return [accountContext?.selectedAccountOrClient?.account?.accountNumber];
    } else if (accountContext?.selectedAccountOrClient?.client) {
      return accountContext
        .getAccountsOfClient(accountContext.selectedAccountOrClient.client)
        .map(account => account.accountNumber);
    }

    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- accountContext is not a dependency, but it's fields
  }, [accountContext?.selectedAccountOrClient, accountContext?.getAccountsOfClient]);

  const clientAccountsForMultiAccount = useMemo(() => {
    if (accountContext?.selectedAccountOrClient?.account?.accountNumber) {
      return accountContext
        .getAccountsOfClientByAccount(accountContext.selectedAccountOrClient.account.accountNumber)
        .map(account => account.accountNumber);
    } else if (accountContext?.selectedAccountOrClient?.client) {
      return accountContext
        .getAccountsOfClient(accountContext.selectedAccountOrClient.client)
        .map(account => account.accountNumber);
    }

    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- accountContext is not a dependency, but it's fields
  }, [accountContext?.selectedAccountOrClient, accountContext?.getAccountsOfClient]);

  const getResourceHeader = useCallback((transactionId?: string): ResourceHeaders => {
    const authState = store.get(appAuthAtom);
    return {
      id: `netbroker:${uuid()}`,
      correlid: uuid(),
      source: Config.NetBroker.Headers.Src,
      srcModule: Config.NetBroker.Headers.SrcModule,
      acceptLanguage: authState.isAuthenticated && authState.language ? authState.language : LanguageType.Hun,
      channel: Config.NetBroker.Headers.ChannelWeb,
      transactionid: transactionId ?? uuid(),
    };
  }, []);

  const getResourceHeaderWithToken = useCallback(
    (transactionId?: string): ResourceHeadersWithToken => {
      const authState = store.get(appAuthAtom);
      if (authState.isAuthenticated && authState.secrets) {
        return {
          ...getResourceHeader(transactionId),
          accountId: accountNumber,
          userid: authState.secrets.IdPayload.userGUID,
        };
      }

      throw new Error('Not authenticated');
    },
    [getResourceHeader, accountNumber],
  );

  const getResourceHeaderWithTokenForAccount = useCallback(
    (targetAccountNumber: string, transactionId?: string): ResourceHeadersWithToken => {
      const authState = store.get(appAuthAtom);
      if (authState.isAuthenticated && authState.secrets) {
        return {
          ...getResourceHeader(transactionId),
          accountId: targetAccountNumber,
          userid: authState.secrets.IdPayload.userGUID,
        };
      }

      throw new Error('Not authenticated');
    },
    [getResourceHeader],
  );

  const getResourceHeaderForMultiAccount = useCallback((): ResourceHeadersOfMultiAccountRequest => {
    const authState = store.get(appAuthAtom);
    if (authState.isAuthenticated && authState.secrets) {
      return {
        ...getResourceHeader(),
        accountId: accountNumberForMultiAccount,
        userid: authState.secrets.IdPayload.userGUID,
      };
    }

    throw new Error('Not authenticated');
  }, [getResourceHeader, accountNumberForMultiAccount]);

  const getResourceHeaderForClient = useCallback((): ResourceHeadersOfMultiAccountRequest => {
    const authState = store.get(appAuthAtom);
    if (authState.isAuthenticated && authState.secrets) {
      return {
        ...getResourceHeader(),
        accountId: clientAccountsForMultiAccount,
        userid: authState.secrets.IdPayload.userGUID,
      };
    }

    throw new Error('Not authenticated');
  }, [getResourceHeader, clientAccountsForMultiAccount]);

  return {
    getResourceHeaderWithToken,
    getResourceHeaderWithTokenForAccount,
    getResourceHeader,
    getResourceHeaderForMultiAccount,
    getResourceHeaderForClient,
  };
};
